import React, { useState } from "react";
import "./css/credit_report_request.scss";

import { useDocument } from "../../../components/loan_tools/loan_tool";
import CREDIT_PROVIDERS from "../../../constants/credit_providers";
import {
  toCreditRequestXml,
  toCreditStatusQueryXml
} from "../../../mismo/credit_request";
import {
  fromCreditResponseXml,
  mergeCreditAccounts,
  parseOrderStatus,
  isOrderReady,
  extractReportDocuments
} from "../../../mismo/credit_response";
import { pullCredit } from "../../../services/credit_service";
import { MARRIED } from "../../../constants/marital_status";

// The spouse's borrower index when this borrower is married to another borrower
// in the file, else -1. Drives the "married to" clarity tag and the couple
// auto-detect on the credit request.
function spouseIndexOf(borrowers, i) {
  const b = borrowers[i];
  if (b && b.marital_status === MARRIED) {
    const m = parseInt(b.married_to, 10);
    if (Number.isInteger(m) && m >= 0 && m < borrowers.length && m !== i) {
      return m;
    }
  }
  return -1;
}

const BUREAUS = [
  { key: "equifax", label: "Equifax" },
  { key: "experian", label: "Experian" },
  { key: "transunion", label: "TransUnion" }
];

const REQUEST_TYPES = [
  { key: "new", label: "Order New Report" },
  { key: "reissue", label: "Reissue Existing" },
  { key: "upgrade", label: "Upgrade Existing" },
  { key: "retrieve", label: "Retrieve Updated" }
];

function maskSsn(ssn) {
  const digits = String(ssn || "").replace(/\D/g, "");
  if (digits.length < 4) return ssn ? "•••" : "—";
  return `•••-••-${digits.slice(-4)}`;
}

/**
 * Request Credit Report screen. Clean, modern layout (not a POINT/DOS grid):
 * pick the credit agency, log in, choose bureaus + report/request type; the
 * borrower data is auto-filled from Applicant Info.
 *
 * The live pull (Submit) needs the secure backend connection, so it is disabled
 * for now - a saved report is imported with "Upload Credit Report (XML)".
 */
export default function CreditReportRequest({ onClose }) {
  const [document, setDocument, meta] = useDocument();
  const applicant = document.applicant;
  const borrowers = applicant.borrowers || [];
  const loProfile = (meta && meta.documentAccountProfile) || {};

  const [providerName, setProviderName] = useState("");
  const [showLogin, setShowLogin] = useState(false);
  const [loggedInAs, setLoggedInAs] = useState("");
  const [bureaus, setBureaus] = useState({
    equifax: true,
    experian: true,
    transunion: true
  });
  // Who this pull is for: the set of borrower indices the LO checked. Defaults to
  // the primary borrower plus their spouse (if married) - one person or one
  // couple at a time. `joint` is derived from the selection, not a manual toggle.
  const [selected, setSelected] = useState(() => {
    const init = { 0: true };
    const sp = spouseIndexOf(borrowers, 0);
    if (sp !== -1) init[sp] = true;
    return init;
  });
  const selectedIndices = Object.keys(selected)
    .filter(k => selected[k])
    .map(Number)
    .sort((a, b) => a - b);
  const selectedBorrowers = selectedIndices.map(i => borrowers[i]).filter(Boolean);
  const joint = selectedBorrowers.length > 1;
  // Payment source: the borrower's card, the LO's card, or auto-pay to the LO's
  // account on file.
  const [payMode, setPayMode] = useState("borrower");
  const [requestType, setRequestType] = useState("new");
  const [creditRef, setCreditRef] = useState("");
  const [copyToDebt, setCopyToDebt] = useState(true);
  const [onlyBalance, setOnlyBalance] = useState(true);
  const [onlyPayment, setOnlyPayment] = useState(true);
  const b0 = borrowers[0] || {};
  const [card, setCard] = useState({
    name: [b0.first_name, b0.last_name].filter(Boolean).join(" "),
    number: "4111 1111 1111 1111",
    expMonth: "12",
    expYear: "2027",
    cvv: "234",
    address: b0.current_address || "",
    city: b0.city || "",
    state: b0.state || "",
    zip: b0.zip_code || "46203"
  });
  const setCardField = (k, v) => setCard(c => ({ ...c, [k]: v }));
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitInfo, setSubmitInfo] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [lastResponseXml, setLastResponseXml] = useState("");
  const [reportDocs, setReportDocs] = useState({ html: "", pdfBase64: "" });

  const borrowerLast =
    (borrowers[0] && borrowers[0].last_name) || "Borrower";

  // One person, or one married couple, per pull. Anything else (two people who
  // aren't a listed couple, or 3+) is blocked so the LO can't mix borrowers.
  const selectionOk =
    selectedIndices.length === 1 ||
    (selectedIndices.length === 2 &&
      spouseIndexOf(borrowers, selectedIndices[0]) === selectedIndices[1]);

  function toggleBorrower(index, checked) {
    setSelected(prev => {
      const next = { ...prev };
      if (checked) next[index] = true;
      else delete next[index];
      return next;
    });
  }

  const fullName = b =>
    [b && b.first_name, b && b.middle_name, b && b.last_name]
      .filter(Boolean)
      .join(" ");

  function printReport() {
    if (!reportDocs.html) return;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(reportDocs.html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 500);
  }

  function savePdf() {
    if (!reportDocs.pdfBase64) return;
    const byteChars = atob(reportDocs.pdfBase64);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      bytes[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement("a");
    a.href = url;
    a.download = `${borrowerLast} Credit Report.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Poll the async order to completion, then return the finished response XML.
  async function pollUntilReady(orderId) {
    const MAX_TRIES = 15;
    const DELAY_MS = 2000;
    let last = "";
    for (let i = 0; i < MAX_TRIES; i++) {
      await new Promise(r => setTimeout(r, DELAY_MS));
      const statusXml = toCreditStatusQueryXml(selectedBorrowers, {
        joint,
        bureaus,
        orderId
      });
      last = await pullCredit(statusXml);
      const st = parseOrderStatus(last);
      if (st.errorText) throw new Error(st.errorText);
      if (isOrderReady(st)) return last;
    }
    return last; // ran out of tries - return whatever we last saw
  }

  function downloadResponse() {
    const body =
      lastResponseXml && lastResponseXml.length
        ? lastResponseXml
        : "<!-- The backend returned an empty response body. -->";
    const blob = new Blob([body], { type: "text/xml" });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement("a");
    a.href = url;
    a.download = "credit-response.xml";
    a.click();
    URL.revokeObjectURL(url);
  }

  const provider = CREDIT_PROVIDERS.find(p => p.name === providerName);

  async function onSubmit() {
    setSubmitError("");
    setSubmitInfo("");
    setSubmitting(true);
    try {
      // 1) Submit the order (returns a VendorOrderIdentifier, status NOT READY).
      const requestXml = toCreditRequestXml(selectedBorrowers, {
        joint,
        bureaus,
        card
      });
      const submitResp = await pullCredit(requestXml);
      setSubmitted(true);
      setLastResponseXml(submitResp || "");
      const submitStatus = parseOrderStatus(submitResp);
      if (submitStatus.errorText) throw new Error(submitStatus.errorText);

      // 2) The order is async - poll StatusQuery until the report is READY.
      //    (If the vendor already returned a finished report, skip polling.)
      let finalResp = submitResp;
      if (!isOrderReady(submitStatus)) {
        if (!submitStatus.orderId) {
          throw new Error(
            "The credit vendor did not return an order number, so the report could not be retrieved."
          );
        }
        setSubmitInfo("Order placed. Waiting for the report to be ready…");
        finalResp = await pollUntilReady(submitStatus.orderId);
        setLastResponseXml(finalResp || "");
      }

      // 3) Pull the printable HTML/PDF documents out of the finished report.
      setReportDocs(extractReportDocuments(finalResp));

      // 4) Import tradelines.
      const len = (finalResp || "").length;
      const { document: parsed } = fromCreditResponseXml(finalResp, {
        ownerIndices: selectedIndices.map(String)
      });
      const accounts = Object.values(parsed.accountSummaries || {});
      if (accounts.length === 0) {
        setSubmitInfo(
          `The report is ready but contained no tradelines to import (response was ${len.toLocaleString()} characters). ` +
            "You can still Print or Save the report, or use Download response to inspect it."
        );
        setSubmitting(false);
        return;
      }
      // Apply the LO's import filters (option to import only real debts vs the
      // full report). Unchecking both brings in every tradeline.
      const numOf = v => {
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
      };
      const toImport = accounts.filter(
        a =>
          (!onlyBalance || numOf(a.current_balance) > 0) &&
          (!onlyPayment || numOf(a.monthly_payment) > 0)
      );
      if (copyToDebt && toImport.length) {
        setDocument(mergeCreditAccounts(document, toImport));
      }
      const skipped = accounts.length - toImport.length;
      setSubmitInfo(
        `Report ready: ${accounts.length} tradeline${
          accounts.length === 1 ? "" : "s"
        } found` +
          (copyToDebt
            ? `, imported ${toImport.length}` +
              (skipped ? ` (${skipped} filtered out)` : "")
            : " (import turned off)") +
          ". You can Print or Save the full report below."
      );
      setSubmitting(false);
    } catch (err) {
      setSubmitted(true);
      setSubmitError(
        (err && err.message) ||
          "Couldn't complete the credit pull. Please try again."
      );
      setSubmitting(false);
    }
  }

  return (
    <div className="crr-overlay">
      <div className="crr-modal">
        <div className="crr-header">
          <span>Request Credit Report</span>
          <button className="crr-x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="crr-body">
          {/* Credit agency + login */}
          <section className="crr-section">
            <div className="crr-label">Credit Agency</div>
            <div className="crr-agency-row">
              <select
                className="crr-select"
                value={providerName}
                onChange={e => {
                  setProviderName(e.target.value);
                  setLoggedInAs("");
                }}
              >
                <option value="">Select a credit company…</option>
                {CREDIT_PROVIDERS.map(p => (
                  <option key={p.mlcid} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
              <button
                className="button"
                disabled={!provider}
                onClick={() => setShowLogin(true)}
              >
                {loggedInAs ? "Logged In" : "Log In"}
              </button>
            </div>
            {provider && (
              <div className="crr-help">
                {loggedInAs && (
                  <span className="crr-loggedin">Signed in as {loggedInAs}</span>
                )}
                <span className="crr-need-help">
                  Need help? {provider.phone}
                  {provider.helpUrl && (
                    <>
                      {" · "}
                      <a
                        href={provider.helpUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Getting Started
                      </a>
                    </>
                  )}
                </span>
              </div>
            )}
          </section>

          {/* Bureaus */}
          <section className="crr-section">
            <div className="crr-label">Bureaus</div>
            <div className="crr-checks">
              {BUREAUS.map(b => (
                <label key={b.key}>
                  <input
                    type="checkbox"
                    checked={bureaus[b.key]}
                    onChange={e =>
                      setBureaus({ ...bureaus, [b.key]: e.target.checked })
                    }
                  />{" "}
                  {b.label}
                </label>
              ))}
            </div>
          </section>

          {/* Request type */}
          <section className="crr-section crr-columns">
            <div>
              <div className="crr-label">Request Type</div>
              <div className="crr-radios">
                {REQUEST_TYPES.map(r => (
                  <label key={r.key}>
                    <input
                      type="radio"
                      name="crr-request-type"
                      checked={requestType === r.key}
                      onChange={() => setRequestType(r.key)}
                    />{" "}
                    {r.label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="crr-label">Credit Reference #</div>
              <input
                className="crr-input"
                value={creditRef}
                placeholder={
                  requestType === "new" ? "Assigned after ordering" : "File #"
                }
                disabled={requestType === "new"}
                onChange={e => setCreditRef(e.target.value)}
              />
            </div>
          </section>

          {/* Who is this report for? */}
          <section className="crr-section">
            <div className="crr-label">
              Who is this report for?{" "}
              <span className="crr-sub">— one person or one married couple per pull</span>
            </div>
            <div className="crr-select-borrowers">
              {borrowers.map((b, i) => {
                const sp = spouseIndexOf(borrowers, i);
                const on = !!selected[i];
                return (
                  <label
                    key={i}
                    className={`crr-borrower-pick ${on ? "picked" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={e => toggleBorrower(i, e.target.checked)}
                    />
                    <span className="crr-pick-body">
                      <span className="crr-pick-name">
                        {fullName(b) || `Borrower ${i + 1}`}
                        {sp !== -1 && (
                          <span className="crr-married-tag">
                            married to {fullName(borrowers[sp])}
                          </span>
                        )}
                      </span>
                      <span className="crr-pick-meta">
                        SSN {maskSsn(b.ssn)} · DOB {b.date_of_birth || "—"}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
            <div
              className={`crr-pick-banner ${
                selectedIndices.length === 0
                  ? "info"
                  : !selectionOk
                  ? "warn"
                  : joint
                  ? "ok"
                  : "accent"
              }`}
            >
              {selectedIndices.length === 0
                ? "Select the borrower(s) this report is for."
                : !selectionOk
                ? "Pick one person, or one married couple, per pull."
                : joint
                ? `Joint report for ${fullName(selectedBorrowers[0])} + ${fullName(
                    selectedBorrowers[1]
                  )} (married couple).`
                : `Individual report for ${fullName(selectedBorrowers[0])}.`}
            </div>
          </section>

          {/* Payment source */}
          <section className="crr-section">
            <div className="crr-label">
              Who pays for this pull?{" "}
              <span className="crr-sub">— the bureau charges per pull</span>
            </div>
            <div className="crr-segment crr-pay-tabs">
              <button
                className={payMode === "borrower" ? "active" : ""}
                onClick={() => {
                  setPayMode("borrower");
                  setCardField("name", fullName(selectedBorrowers[0]));
                }}
              >
                Borrower&apos;s card
              </button>
              <button
                className={payMode === "lo" ? "active" : ""}
                onClick={() => {
                  setPayMode("lo");
                  setCardField(
                    "name",
                    [loProfile.first_name, loProfile.last_name]
                      .filter(Boolean)
                      .join(" ")
                  );
                }}
              >
                LO&apos;s card
              </button>
              <button
                className={payMode === "auto" ? "active" : ""}
                onClick={() => setPayMode("auto")}
              >
                Auto-pay (LO account)
              </button>
            </div>
          </section>

          {/* Payment card — the credit bureau charges per pull */}
          {payMode === "auto" ? (
            <section className="crr-section">
              <div className="crr-autopay-note">
                Charged to your billing account on file
                {loProfile.company_name ? ` (${loProfile.company_name})` : ""}. No
                card entry needed.
              </div>
            </section>
          ) : (
          <section className="crr-section">
            <div className="crr-label">
              {payMode === "lo" ? "LO's payment card" : "Borrower's payment card"}{" "}
              <span className="crr-sub">— the bureau charges per pull</span>
            </div>
            <div className="crr-card">
              <label className="crr-card-wide">
                Cardholder Name
                <input
                  className="crr-input"
                  value={card.name}
                  onChange={e => setCardField("name", e.target.value)}
                />
              </label>
              <label className="crr-card-wide">
                Card Number
                <input
                  className="crr-input"
                  value={card.number}
                  onChange={e => setCardField("number", e.target.value)}
                />
              </label>
              <label>
                Exp. Month
                <input
                  className="crr-input"
                  value={card.expMonth}
                  placeholder="MM"
                  onChange={e => setCardField("expMonth", e.target.value)}
                />
              </label>
              <label>
                Exp. Year
                <input
                  className="crr-input"
                  value={card.expYear}
                  placeholder="YYYY"
                  onChange={e => setCardField("expYear", e.target.value)}
                />
              </label>
              <label>
                CVV
                <input
                  className="crr-input"
                  value={card.cvv}
                  onChange={e => setCardField("cvv", e.target.value)}
                />
              </label>
              <label className="crr-card-wide">
                Billing Address
                <input
                  className="crr-input"
                  value={card.address}
                  onChange={e => setCardField("address", e.target.value)}
                />
              </label>
              <label>
                City
                <input
                  className="crr-input"
                  value={card.city}
                  onChange={e => setCardField("city", e.target.value)}
                />
              </label>
              <label>
                State
                <input
                  className="crr-input"
                  value={card.state}
                  onChange={e => setCardField("state", e.target.value)}
                />
              </label>
              <label>
                Billing ZIP
                <input
                  className="crr-input"
                  value={card.zip}
                  onChange={e => setCardField("zip", e.target.value)}
                />
              </label>
            </div>
          </section>
          )}

          <label className="crr-copy">
            <input
              type="checkbox"
              checked={copyToDebt}
              onChange={e => setCopyToDebt(e.target.checked)}
            />{" "}
            Copy report liabilities to the Debt page
          </label>

          {copyToDebt && (
            <div className="crr-import-filters">
              <div className="crr-import-filters-title">
                What to import <span className="crr-sub">— uncheck both to import the full report</span>
              </div>
              <label>
                <input
                  type="checkbox"
                  checked={onlyBalance}
                  onChange={e => setOnlyBalance(e.target.checked)}
                />{" "}
                Only accounts with a balance
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={onlyPayment}
                  onChange={e => setOnlyPayment(e.target.checked)}
                />{" "}
                Only accounts with a monthly payment
              </label>
            </div>
          )}
        </div>

        <div className="crr-footer">
          <div className="crr-footnote">
            {submitError ? (
              <span className="crr-error">{submitError}</span>
            ) : submitInfo ? (
              <span>{submitInfo}</span>
            ) : (
              <>
                Submitting orders the report and copies the debts to the Debt
                page. A saved report can also be imported with{" "}
                <b>Upload Credit Report (XML)</b>.
              </>
            )}
          </div>
          <div className="crr-actions">
            {reportDocs.html ? (
              <button
                className="button inverse-button"
                onClick={printReport}
                title="Open the full credit report and print it"
              >
                Print Report
              </button>
            ) : null}
            {reportDocs.pdfBase64 ? (
              <button
                className="button inverse-button"
                onClick={savePdf}
                title="Save the full credit report as a PDF"
              >
                Save PDF
              </button>
            ) : null}
            {submitted ? (
              <button
                className="button inverse-button"
                onClick={downloadResponse}
                title="Download the raw credit response for troubleshooting"
              >
                Download response
              </button>
            ) : null}
            <button className="button inverse-button" onClick={onClose}>
              Cancel
            </button>
            <button
              className="button"
              disabled={!provider || submitting || !selectionOk}
              onClick={onSubmit}
              title={
                !provider
                  ? "Choose a credit agency first."
                  : !selectionOk
                  ? "Pick one person or one married couple."
                  : "Order the credit report"
              }
            >
              {submitting
                ? "Pulling…"
                : joint
                ? "Order joint report"
                : "Order individual report"}
            </button>
          </div>
        </div>
      </div>

      {showLogin && provider && (
        <LoginDialog
          provider={provider}
          onCancel={() => setShowLogin(false)}
          onSave={userId => {
            setLoggedInAs(userId);
            setShowLogin(false);
          }}
        />
      )}
    </div>
  );
}

/** Provider login popup. Mirrors POINT: User ID / Password / Save. */
function LoginDialog({ provider, onCancel, onSave }) {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [save, setSave] = useState(true);

  return (
    <div className="crr-login-overlay">
      <div className="crr-login">
        <div className="crr-login-header">
          {provider.name} — Log In
          <button className="crr-x" onClick={onCancel} aria-label="Close">
            ×
          </button>
        </div>
        <div className="crr-login-body">
          <label>
            User ID
            <input value={userId} onChange={e => setUserId(e.target.value)} />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
          </label>
          <label className="crr-login-save">
            <input
              type="checkbox"
              checked={save}
              onChange={e => setSave(e.target.checked)}
            />{" "}
            Save my credentials for this company
          </label>
          <div className="crr-login-help">
            <div className="crr-login-help-title">Need help?</div>
            <div>{provider.name}</div>
            {provider.phone && <div>{provider.phone}</div>}
            {provider.helpUrl && (
              <a
                href={provider.helpUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Getting Started
              </a>
            )}
          </div>
        </div>
        <div className="crr-login-actions">
          <button className="button inverse-button" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="button"
            disabled={!userId}
            onClick={() => onSave(userId)}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
